// const SCALE_FACTOR = 1;

// Game Configuration Constants
export const GAME_CONFIG = {
  WIDTH: 1392,
  HEIGHT: 1632,
  BACKGROUND_COLOR: "#004480",
  BLOCK_SIZE: 48,
  SPEED: 510,
  GHOST_SPEED_MULTIPLIER: 0.3,
  SCARED_GHOST_SPEED_MULTIPLIER: 0.2,
} as const;

// Game Mode Durations (in milliseconds)
export const MODE_DURATIONS = {
  SCATTER_MODE: 7000,
  CHASE_MODE: 20000,
  SCARED_MODE: 9000,
  ENTRY_DELAY: 7000,
  RESPAWN_DELAY: 5000,
} as const;

const RIGHT_LAST_TILE = 1296;
const LEFT_LAST_TILE = 96;
const TOP_LAST_TILE = 96;
const BOTTOM_LAST_TILE = 1536;

// Ghost Scatter Targets (corner positions)
export const GHOST_SCATTER_TARGETS = {
  PINKY: { x: RIGHT_LAST_TILE, y: TOP_LAST_TILE },
  BLINKY: { x: LEFT_LAST_TILE, y: TOP_LAST_TILE },
  INKY: { x: RIGHT_LAST_TILE, y: BOTTOM_LAST_TILE },
  CLYDE: { x: LEFT_LAST_TILE, y: BOTTOM_LAST_TILE },
} as const;

// Ghost House Boundaries
export const GHOST_HOUSE = {
  LEFT: 624,
  RIGHT: 786,
  TOP: 672,
  BOTTOM: 768,
  ENTRANCE_X: 696,
  ENTRANCE_Y: 624,
} as const;

// Ghost Starting Positions
export const GHOST_START_POSITIONS = {
  PINK: { x: 696, y: 726 },
  ORANGE: { x: 630, y: 726 },
  RED: { x: 696, y: 726 },
  BLUE: { x: 765, y: 726 },
} as const;

// Pacman Starting Position
export const PACMAN_START_POSITION = {
  X: 690,
  Y: 1152,
} as const;

// Power Pill Positions
export const POWER_PILL_POSITIONS = [
  { x: 96, y: 288 },
  { x: 1296, y: 288 },
  { x: 96, y: 1296 },
  { x: 1296, y: 1296 },
] as const;

// Animation Frame Rates
export const ANIMATION_FRAME_RATES = {
  PACMAN: 10,
  GHOST: 10,
  PACMAN_DEATH: 10,
  GHOST_BLINK: 20,
} as const;

// Game Timing Constants
export const TIMING = {
  GHOST_ENTRY_DELAY: 5000,
  DEATH_ANIMATION_DURATION: 2000,
  GHOST_RESPAWN_DELAY: 1000,
  STUCK_TIMER_THRESHOLD: 30,
  BLINK_START_DELAY: 2000, // Start blinking 2 seconds before scared mode ends
  DOTS_REGENERATION_INTERVAL: 5000, // Regenerate dots and power pills every 30 seconds
  WANDERING_DELAY: 500, // Time in ms before Pacman is considered wandering
} as const;

// Sprite Dimensions
export const SPRITE_DIMENSIONS = {
  FRAME_WIDTH: 96,
  FRAME_HEIGHT: 96,
} as const;

// Chase Mode Offsets
export const CHASE_OFFSETS = {
  PINKY_AHEAD: 4, // 4 blocks ahead
  INKY_AHEAD: 2, // 2 blocks ahead
  CLYDE_DISTANCE_THRESHOLD: 8, // 8 blocks distance threshold
} as const;

// Direction Constants
export const DIRECTIONS = {
  LEFT: "left",
  RIGHT: "right",
  UP: "up",
  DOWN: "down",
  NULL: "null",
} as const;

// Game Modes
export const GAME_MODES = {
  SCATTER: "scatter",
  CHASE: "chase",
  SCARED: "scared",
} as const;

// Ghost Texture Keys
export const GHOST_TEXTURES = {
  PINK: "pinkGhost",
  ORANGE: "orangeGhost",
  RED: "redGhost",
  BLUE: "blueGhost",
  SCARED: "scaredGhost",
  SCARED_WHITE: "scaredGhostWhite",
} as const;

export const ASSET_KEYS = {
  MAP: "map",
  TILESET: "pacman tileset",
  PACMAN: "pacman0",
  PACMAN1: "pacman1",
  PACMAN2: "pacman2",
  PACMAN3: "pacman3",
  PACMAN4: "pacman4",
  PACMAN5: "pacman5",
  PACMAN_DEATH1: "pacmanDeath1",
  PACMAN_DEATH2: "pacmanDeath2",
  PACMAN_DEATH3: "pacmanDeath3",
  PACMAN_DEATH4: "pacmanDeath4",
  PACMAN_DEATH5: "pacmanDeath5",
  PACMAN_DEATH6: "pacmanDeath6",
  PACMAN_DEATH7: "pacmanDeath7",
  PACMAN_DEATH8: "pacmanDeath8",
  PACMAN_DEATH9: "pacmanDeath9",
  PACMAN_DEATH10: "pacmanDeath10",
  PACMAN_DEATH11: "pacmanDeath11",
  PACMAN_DEATH12: "pacmanDeath12",
  PINK_GHOST: "pinkGhost",
  PINK_GHOST1: "pinkGhost1",
  ORANGE_GHOST: "orangeGhost",
  ORANGE_GHOST1: "orangeGhost1",
  RED_GHOST: "redGhost",
  RED_GHOST1: "redGhost1",
  BLUE_GHOST: "blueGhost",
  BLUE_GHOST0: "blueGhost0",
  BLUE_GHOST1: "blueGhost1",
  AFRAID_GHOST: "afraidGhost",
  SCARED_GHOST: "scaredGhost",
  SCARED_GHOST_WHITE: "scaredGhostWhite",
  LIFE_COUNTER: "lifeCounter",

  DOT: "dot",
  POWER_PILL: "powerPill",
} as const;

// Asset Paths
export const ASSET_PATHS = {
  TILESET: "pac_man_tiles/tileset.png",
  MAP: "pacman-map.json",
  PACMAN: "pacman_characters/pacman/pacman0.png",
  PACMAN1: "pacman_characters/pacman/pacman1.png",
  PACMAN2: "pacman_characters/pacman/pacman2.png",
  PACMAN3: "pacman_characters/pacman/pacman3.png",
  PACMAN4: "pacman_characters/pacman/pacman4.png",
  PACMAN5: "pacman_characters/pacman/pacman5.png",
  PACMAN_DEATH1: "pacman_life_death/pacman_death/spr_pacdeath_0.png",
  PACMAN_DEATH2: "pacman_life_death/pacman_death/spr_pacdeath_1.png",
  PACMAN_DEATH3: "pacman_life_death/pacman_death/spr_pacdeath_2.png",
  PACMAN_DEATH4: "pacman_life_death/pacman_death/spr_pacdeath_3.png",
  PACMAN_DEATH5: "pacman_life_death/pacman_death/spr_pacdeath_4.png",
  PACMAN_DEATH6: "pacman_life_death/pacman_death/spr_pacdeath_5.png",
  PACMAN_DEATH7: "pacman_life_death/pacman_death/spr_pacdeath_6.png",
  PACMAN_DEATH8: "pacman_life_death/pacman_death/spr_pacdeath_7.png",
  PACMAN_DEATH9: "pacman_life_death/pacman_death/spr_pacdeath_8.png",
  PACMAN_DEATH10: "pacman_life_death/pacman_death/spr_pacdeath_9.png",
  PACMAN_DEATH11: "pacman_life_death/pacman_death/spr_pacdeath_10.png",
  PACMAN_DEATH12: "pacman_life_death/pacman_death/spr_pacdeath_11.png",
  DOT: "pacman_items/dot.png",
  POWER_PILL: "pacman_items/spr_power_pill_0.png",
  PINK_GHOST: "ghost/pink_ghost/spr_ghost_pink_0.png",
  PINK_GHOST1: "ghost/pink_ghost/spr_ghost_pink_1.png",
  ORANGE_GHOST: "ghost/orange_ghost/spr_ghost_orange_0.png",
  ORANGE_GHOST1: "ghost/orange_ghost/spr_ghost_orange_1.png",
  BLUE_GHOST: "ghost/blue_ghost/spr_ghost_blue_0.png",
  BLUE_GHOST1: "ghost/blue_ghost/spr_ghost_blue_1.png",
  RED_GHOST: "ghost/red_ghost/spr_ghost_red_0.png",
  RED_GHOST1: "ghost/red_ghost/spr_ghost_red_1.png",
  SCARED_GHOST: "ghost/ghost_afraid/spr_afraid_0.png",
  SCARED_GHOST_WHITE: "ghost/ghost_afraid/spr_afraid_1.png",
  LIFE_COUNTER: "pacman_life_death/pacman_life_counter/spr_lifecounter_0.png",
} as const;

// Animation Keys
export const ANIMATION_KEYS = {
  PACMAN_ANIM: "pacmanAnim",
  PACMAN_DEATH: "pacmanDeath",
  PINK_GHOST_ANIM: "pinkGhostAnim",
  ORANGE_GHOST_ANIM: "orangeGhostAnim",
  RED_GHOST_ANIM: "redGhostAnim",
  BLUE_GHOST_ANIM: "blueGhostAnim",
} as const;

// Initial Game State
export const INITIAL_GAME_STATE = {
  LIVES: 3,
  IS_PACMAN_ALIVE: true,
  HAS_RESPAWNED: false,
  CURRENT_MODE: GAME_MODES.SCATTER,
  DIRECTION: DIRECTIONS.NULL,
  PREVIOUS_DIRECTION: DIRECTIONS.LEFT,
} as const;

// Map Layer Names
export const MAP_LAYERS = {
  TILE_LAYER_1: "Tile Layer 1",
} as const;

// Physics Configuration
export const PHYSICS_CONFIG = {
  GRAVITY_X: 0,
  GRAVITY_Y: 0,
  DEBUG: false,
} as const;

// Scale Configuration
export const SCALE_CONFIG = {
  MODE: "FIT",
  AUTO_CENTER: "CENTER_BOTH",
} as const;

// Rendering Depth/Z-Index Configuration
export const RENDER_DEPTHS = {
  BACKGROUND: 0,
  DOTS: 1,
  POWER_PILLS: 1,
  PILLS: 1,
  PACMAN: 2,
  GHOSTS: 2,
  UI: 3,
} as const;

// Pill Configuration
export const PILL_CONFIG = {
  CORRECT_ANSWER_POINTS: 100, // Points awarded for correct answer
  MIN_HORIZONTAL_LENGTH: 3, // Minimum tiles for 3-digit pills
  PILL_HEIGHT: 72, // Height of pill sprite
  PILL_WIDTH_2DIGIT: 96, // Width for 2-digit pills
  PILL_WIDTH_3DIGIT: 144, // Width for 3-digit pills
} as const;

// Sound Keys
export const SOUND_KEYS = {
  START_MUSIC: "start-music",
  COFFEE_BREAK: "coffee-break",
  EATING_GHOST: "eating-ghost",
  KILLED_BY_GHOST: "killed-by-ghost",
  GHOST_NORMAL_MOVE: "ghost-normal-move",
  GHOST_SCARED_MODE: "ghost-scared-mode",
  GHOST_RETURN_HOME: "ghost-return-home",
  NEW_LIFE: "new-life",
  CREDIT_SOUND: "credit-sound",
  CORRECT_ANSWER: "correct-answer",
} as const;
